import CloseIcon from "@mui/icons-material/Close";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";
import { Box, Button, Drawer, IconButton, Stack, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import { useState } from "react";
import type { CharityCause } from "@/interfaces/charity";

/**
 * The cause detail slide-in — Stage 4's charity panel.
 *
 * This is HOW A CAUSE IS ADDED. Cards in the grid open this panel rather than toggling selection
 * directly, so the client reads who the charity is and what the money does before committing. The
 * button is a toggle: "Add this cause" becomes "Added — remove" once it is in the set.
 *
 * The story is HTML straight from Shopify's `impact.description` metafield, so it renders as
 * authored rather than being flattened to text.
 *
 * A SLIDE-IN, not a full-screen takeover: 66.67% wide capped at 960px, anchored right, with the
 * grid still visible behind it. That is the prototype's `.charity-slidein`, and it matters — the
 * client is comparing causes, so losing sight of the grid turns a browse into a commitment.
 */
export default function CauseDetailPanel({
  cause,
  selected,
  onToggle,
  onClose,
}: {
  cause: CharityCause | null;
  selected: boolean;
  onToggle: (causeProductId: string) => void;
  onClose: () => void;
}) {
  // Starts on the primary photo. The parent keys this component by cause id, so opening a
  // different cause remounts it and the index resets — no effect, and no frame where the previous
  // cause's photo is still showing.
  const [active, setActive] = useState(0);

  if (!cause) return null;
  const images = cause.gallery.length > 0 ? cause.gallery : [cause.imageUrl ?? ""];
  const main = images[Math.min(active, images.length - 1)] ?? "";

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      // Above the MODAL layer, not the drawer layer. MUI puts Drawer at z-index 1200 and Dialog at
      // 1300, so a panel opened from inside a dialog — which is exactly how the seed flow uses it —
      // renders BEHIND the dialog that launched it.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      PaperProps={{
        sx: { width: "66.67%", maxWidth: 960, bgcolor: "#fff", backgroundImage: "none" },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close"
        sx={{ position: "absolute", top: 16, right: 20, zIndex: 2 }}
      >
        <CloseIcon />
      </IconButton>

      <Stack direction={{ xs: "column", md: "row" }} sx={{ height: "100%", overflow: "auto" }}>
        {/* Left — the polaroid and its gallery. */}
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            flex: 1,
            p: 4,
            borderRight: { md: "1px solid var(--color-card-border)" },
            minWidth: 0,
          }}
        >
          {/* No frame drawn here either — the primary Shopify image carries its own. See the note
              on CauseSelector's photo. */}
          <Box
            component="img"
            src={main}
            alt=""
            sx={{
              display: "block",
              width: 340,
              maxWidth: "70vw",
              height: 300,
              objectFit: "cover",
              borderRadius: "2px",
            }}
          />

          {images.length > 1 && (
            <Stack direction="row" spacing={1} sx={{ mt: 4 }}>
              {images.slice(0, 5).map((url, i) => (
                <Box
                  key={url}
                  component="img"
                  src={url}
                  alt=""
                  onClick={() => setActive(i)}
                  sx={{
                    width: 78,
                    height: 58,
                    objectFit: "cover",
                    cursor: "pointer",
                    borderRadius: 1,
                    border: `2px solid ${i === active ? "var(--color-dark-dust)" : "transparent"}`,
                    p: "2px",
                  }}
                />
              ))}
            </Stack>
          )}
        </Stack>

        {/* Right — who they are, what the money does, and the add toggle. */}
        <Box sx={{ flex: 1, p: { xs: 3, md: 4.5 }, pt: { xs: 6, md: 7 }, minWidth: 0 }}>
          <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 30, lineHeight: 1.15 }}>
            {cause.title}
          </Typography>
          <Typography
            sx={{
              fontSize: 12.5,
              letterSpacing: "1.3px",
              textTransform: "uppercase",
              fontWeight: 700,
              color: "var(--color-dust-ink)",
              mt: 1.5,
            }}
          >
            {cause.organization}
          </Typography>

          <Button
            onClick={() => onToggle(cause.causeProductId)}
            startIcon={<VolunteerActivismOutlinedIcon />}
            sx={{
              mt: 3,
              px: 3,
              py: 1.3,
              borderRadius: 1,
              bgcolor: selected ? "var(--color-dark-dust)" : "var(--color-rodeo-black)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "1.4px",
              "&:hover": { bgcolor: "var(--color-dust-ink)" },
            }}
          >
            {selected ? "Added — remove" : "Add this cause"}
          </Button>

          {cause.story ? (
            <Box
              sx={{
                mt: 4,
                fontSize: 16,
                lineHeight: 1.65,
                color: "var(--color-rodeo-black)",
                "& p": { m: 0, mb: 2 },
              }}
              // Shopify's own authored copy, from the impact.description metafield. Sanitised the
              // same way the email preview treats RTE HTML: the source is GFG's own store, but it
              // reaches us over an API and renders inside an authenticated session.
              // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitised above by DOMPurify
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cause.story) }}
            />
          ) : (
            <Typography sx={{ mt: 4, fontSize: 15, color: "var(--color-graphite)" }}>
              Impact information coming soon.
            </Typography>
          )}
        </Box>
      </Stack>
    </Drawer>
  );
}
